import Papa from 'papaparse';
import type { ParsedData, ColumnInfo, ColumnType } from '../types';

/**
 * Try to parse a date value using multiple format strategies
 * Supports:
 * - ISO 8601 with time and timezone (YYYY-MM-DDTHH:mm:ss.sssZ)
 * - ISO 8601 dates (YYYY-MM-DD)
 * - US format (MM/DD/YYYY)
 * - European format (DD/MM/YYYY)
 * - Compact format (YYYYMMDD)
 * - Unix timestamps (seconds and milliseconds)
 * - Natural language dates (via native Date parsing)
 * - Datetime with timezone offsets
 */
export function tryParseDate(value: unknown): Date | null {
  if (!value || value === '') return null;

  const str = String(value).trim();

  // Strategy 1: Check for Unix timestamps first (before native parsing)
  const timestamp = Number(value);
  if (!isNaN(timestamp) && str === String(timestamp)) {
    // Unix timestamp in milliseconds (between 2000-01-01 and 2100-01-01)
    if (timestamp > 946684800000 && timestamp < 4102444800000) {
      return new Date(timestamp);
    }
    // Unix timestamp in seconds
    if (timestamp > 946684800 && timestamp < 4102444800) {
      return new Date(timestamp * 1000);
    }
  }

  // Strategy 2: ISO 8601 with time and timezone
  // Examples:
  // - 2024-01-15T14:30:00Z
  // - 2024-01-15T14:30:00.123Z
  // - 2024-01-15T14:30:00+00:00
  // - 2024-01-15T14:30:00-05:00
  // - 2024-01-15 14:30:00 (space instead of T)
  const iso8601Pattern =
    /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?$/;
  if (iso8601Pattern.test(str)) {
    const date = new Date(str);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  const isoDateOnlyMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (isoDateOnlyMatch) {
    const year = parseInt(isoDateOnlyMatch[1], 10);
    const month = parseInt(isoDateOnlyMatch[2], 10) - 1;
    const day = parseInt(isoDateOnlyMatch[3], 10);
    const date = new Date(year, month, day);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Strategy 3: Common datetime formats with time
  // Examples:
  // - 2024-01-15 14:30:00
  // - 01/15/2024 14:30:00
  // - 15/01/2024 14:30:00
  const datetimePatterns = [
    // YYYY-MM-DD HH:mm:ss
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/,
    // MM/DD/YYYY HH:mm:ss or DD/MM/YYYY HH:mm:ss
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/,
  ];

  for (const pattern of datetimePatterns) {
    const match = str.match(pattern);
    if (match) {
      let year: number, month: number, day: number;
      const hour = parseInt(match[4], 10);
      const minute = parseInt(match[5], 10);
      const second = match[6] ? parseInt(match[6], 10) : 0;

      if (pattern.source.startsWith('^(\\d{4})')) {
        // YYYY-MM-DD format
        year = parseInt(match[1], 10);
        month = parseInt(match[2], 10) - 1;
        day = parseInt(match[3], 10);
      } else {
        // MM/DD/YYYY or DD/MM/YYYY
        const first = parseInt(match[1], 10);
        const second = parseInt(match[2], 10);
        year = parseInt(match[3], 10);

        if (first > 12) {
          day = first;
          month = second - 1;
        } else if (second > 12) {
          month = first - 1;
          day = second;
        } else {
          // Ambiguous - assume MM/DD/YYYY
          month = first - 1;
          day = second;
        }
      }

      const date = new Date(year, month, day, hour, minute, second);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  // Strategy 4: Native Date parsing (handles ISO, common formats, timezones)
  const nativeDate = new Date(str);
  if (!isNaN(nativeDate.getTime())) {
    return nativeDate;
  }

  // Strategy 5: Common date formats with regex (date only, no time)
  const dateFormats = [
    // YYYY-MM-DD or YYYY/MM/DD
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/,
    // DD-MM-YYYY or DD/MM/YYYY
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/,
    // MM-DD-YYYY or MM/DD/YYYY
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/,
    // YYYYMMDD
    /^(\d{4})(\d{2})(\d{2})$/,
  ];

  for (const format of dateFormats) {
    const match = str.match(format);
    if (match) {
      let year: number, month: number, day: number;

      if (format.source.startsWith('^(\\d{4})')) {
        // YYYY-MM-DD format
        year = parseInt(match[1], 10);
        month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
        day = parseInt(match[3], 10);
      } else {
        // Try DD-MM-YYYY (European) or MM-DD-YYYY (US)
        const first = parseInt(match[1], 10);
        const second = parseInt(match[2], 10);
        year = parseInt(match[3], 10);

        // Heuristic: if first number > 12, it's likely day-first
        if (first > 12) {
          day = first;
          month = second - 1;
        } else if (second > 12) {
          month = first - 1;
          day = second;
        } else {
          // Ambiguous - assume MM-DD-YYYY (US format)
          month = first - 1;
          day = second;
        }
      }

      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  return null;
}

/**
 * Detect column type based on sample values
 */
function detectColumnType(values: unknown[]): ColumnType {
  const nonNullValues = values.filter(v => v !== null && v !== undefined && v !== '');

  if (nonNullValues.length === 0) return 'unknown';

  // Check if all values are numeric (but not timestamps/dates)
  const numericCount = nonNullValues.filter(v => {
    const num = Number(v);
    return !isNaN(num) && String(v).trim() !== '';
  }).length;

  // Check if values look like dates using our enhanced parser
  const dateCount = nonNullValues.filter(v => tryParseDate(v) !== null).length;

  // If most values parse as dates, it's a date column
  if (dateCount === nonNullValues.length && dateCount > 0) {
    return 'date';
  }

  // If most values are numeric and not dates, it's numeric
  if (numericCount === nonNullValues.length && numericCount > 0) {
    return 'numeric';
  }

  return 'categorical';
}

/**
 * Parse CSV file
 */
export async function parseCSV(file: File): Promise<ParsedData> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: results => {
        try {
          const rows = results.data as Record<string, unknown>[];
          const columns: ColumnInfo[] = [];

          if (rows.length === 0) {
            throw new Error('CSV file is empty');
          }

          // Analyze columns
          const columnNames = Object.keys(rows[0]);
          for (const colName of columnNames) {
            const columnValues = rows.map(row => row[colName]);
            const sampleValues = columnValues.slice(0, 10) as (string | number)[];
            const missingCount = columnValues.filter(
              v => v === null || v === undefined || v === ''
            ).length;

            columns.push({
              name: colName,
              type: detectColumnType(columnValues),
              sampleValues,
              missingCount,
            });
          }

          resolve({
            columns,
            rows,
            rowCount: rows.length,
            fileName: file.name,
            fileSize: file.size,
          });
        } catch (error) {
          reject(error);
        }
      },
      error: error => {
        reject(new Error(`CSV parsing error: ${error.message}`));
      },
    });
  });
}

/**
 * Parse file based on extension
 */
export async function parseFile(file: File): Promise<ParsedData> {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'csv') {
    return parseCSV(file);
  }

  throw new Error(`Unsupported file type: ${extension}. Only CSV files are supported.`);
}

/**
 * Validate file before parsing
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  const maxSize = 50 * 1024 * 1024; // 50MB
  const allowedExtensions = ['csv'];
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (!extension || !allowedExtensions.includes(extension)) {
    return {
      valid: false,
      error: `Invalid file type. Only CSV files are supported.`,
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds 50MB limit`,
    };
  }

  return { valid: true };
}
